import StaticReportsLayout from '@/Layouts/StaticReportsLayout'
import RiskData from '@/components/WhatIfAnalysis/RiskData'
import SectorComp from '@/components/WhatIfAnalysis/SectorComp'
import SummaryChar from '@/components/WhatIfAnalysis/SummaryChar'
import TrasReport from '@/components/WhatIfAnalysis/TransactionReportData'
import {
    RiskDataItem,
    saveWhatIfPortfolio,
    SaveWhatIfPortfolioRequest,
    SectorCompositionItem,
    SummaryTableData,
    WhatIfHolding,
} from '@/services/whatIfAnalysis.service'
import {
    validateInput,
    ValidationPresets,
} from '@/utils/UserInputValidation'
import { ArrowBack } from '@mui/icons-material'
import {
    Box,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Tab,
    Tabs,
    TextField,
    Typography,
} from '@mui/material'
import dayjs from 'dayjs'
import React, { useEffect, useState } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'

interface WhatIfReportsPageProps {
  title?: string
}

const WhatIfSummaryDashboard: React.FC<WhatIfReportsPageProps> = ({
  title,
}) => {
  const navigate = useNavigate()
  const location = useLocation()
  const [summaryData, setSummaryData] = useState<SummaryTableData[]>([])
  const [riskData, setRiskData] = useState<RiskDataItem[]>([])
  const [sectorData, setSectorData] = useState<SectorCompositionItem[]>([])
  const [securities, setSecurities] = useState<any[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState(0)
  const [portfolioInfo, setPortfolioInfo] = useState<any>(null)
  const [portfolios, setPortfolios] = useState<any[]>([])
  const [saveDialogOpen, setSaveDialogOpen] = useState<boolean>(false)
  const [newPortfolioName, setNewPortfolioName] = useState<string>('')
  const [validationError, setValidationError] = useState<string | null>(null)
  const [isTouched, setIsTouched] = useState(false)
  const [notificationMessage, setNotificationMessage] = useState<string | null>(null)
  const [portfolioSaved, setPortfolioSaved] = useState<boolean>(false)

  const handleBackToPortfolio = () => {
    navigate('/what-if-analysis', {
      state: {
        restore: true,
        refreshPortfolios: portfolioSaved, // Only refresh if portfolio was saved
        selectedPortfolio: portfolioInfo,
        securities,
        portfolios, // Orginal portfolio list to display when no refresh needed
      },
    })
  }

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
  }

  const handleCloseSaveDialog = () => {
    setSaveDialogOpen(false)
    setNewPortfolioName('')
    setValidationError(null)
    setIsTouched(false) 
  }

  
  const handleSaveClick = () => {
    if (!newPortfolioName) {
      setValidationError('Name is required')
      setIsTouched(true)
      return
    }

    const validation = validateInput(newPortfolioName, ValidationPresets.NAME)
    if (!validation.isValid) {
      setValidationError(validation.error || 'Invalid name')
      setIsTouched(true)
      return
    }

    if (newPortfolioName.toLowerCase() === portfolioInfo?.name.toLowerCase()) {
      setValidationError('The new portfolio name cannot be the same as the original. Please choose a different name and try again.')
      setIsTouched(true)
      return
    }

    const isDuplicate = portfolios.some(
      (p) => p.name.toLowerCase() === newPortfolioName.toLowerCase(),
    )

    if (isDuplicate) {
      setValidationError('A portfolio with this name already exists. Please choose a different name.')
      setIsTouched(true)
      return
    }

    handleSavePortfolio()
  }

  const handleSavePortfolio = async () => {
    if (!portfolioInfo || !newPortfolioName) return

    try {
      const formattedDate = dayjs(portfolioInfo.date).format('YYYYMMDD')
      const holdings: WhatIfHolding[] = securities.map((sec: any) => ({
        axsid: Number(sec.axsid) || 0,
        hld: (sec.holdings_current || 0) + sec.holdings_whatif,
      }))

      const saveRequest: SaveWhatIfPortfolioRequest = {
        replaceIfExists: false,
        portDate: Number(formattedDate),
        portName: portfolioInfo.name,
        whatIfPortName: newPortfolioName,
        holdings,
      }

      const response = await saveWhatIfPortfolio(saveRequest)

      if (response.success) {
        setSaveDialogOpen(false)
        setNewPortfolioName('')
        setPortfolioSaved(true) // Mark that portfolio was saved
        setNotificationMessage(`Portfolio saved as ${newPortfolioName}`)
        setTimeout(() => setNotificationMessage(null), 6000)
      } else {
        // Show the error message from the response
        setValidationError(response.message || 'Failed to save the portfolio. Please try again.')
        setIsTouched(true)
      }
    } catch (error) {
      console.error('Error saving portfolio:', error)
      setValidationError('Network error. Please try again.')
      setIsTouched(true)
    }
  }

  // Get data from navigation state
  useEffect(() => {
    const navigationState = location.state as any

    if (navigationState?.summaryData) {
      setSummaryData(navigationState.summaryData)
      setRiskData(navigationState.riskData || [])
      setSectorData(navigationState.sectorData || [])
      setSecurities(navigationState.securities || [])
      setPortfolioInfo(navigationState.selectedPortfolio)
      setPortfolios(navigationState.portfolios || [])
      setPortfolioSaved(false) 
      setLoading(false)
      setError(null)
    } else {
      setSummaryData([])
      setRiskData([])
      setSectorData([])
      setSecurities([])
      setPortfolioInfo(null)
      setLoading(false)
      setError(null)
    }
  }, [location.state])

  return (
    <StaticReportsLayout title={title}>
      <>
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box
            sx={{
              p: 2,
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box>
              <Typography variant='h2' sx={{ mb: 0 }}>
                What-If Analysis Report
              </Typography>
              {portfolioInfo && (
                <Typography
                  variant='h5'
                  sx={{ color: '#1565c0', fontWeight: 'normal', mt: 0.5 }}
                >
                  {portfolioInfo.name} -{' '}
                  {new Date(portfolioInfo.date).toLocaleDateString('en-US', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                  })}
                </Typography>
              )}
            </Box>
            <Button
              variant='outlined'
              startIcon={<ArrowBack />}
              onClick={handleBackToPortfolio}
              sx={{
                textTransform: 'none',
                fontSize: '0.875rem',
                fontWeight: 500,
                color: '#1976d2',
                borderColor: '#1976d2',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                  borderColor: '#1565c0',
                },
              }}
            >
              Back to What-If Portfolio
            </Button>
          </Box>

          {/* Content area - Horizontal layout */}
          <Box sx={{ flex: 1, display: 'flex', gap: 2, minHeight: 0 }}>
            {/* Left Column - Summary Table and Charts (50% width) */}
            <Box
              sx={{
                width: '40%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              {/* Summary Characteristics Table */}
              <Box sx={{ height: '450px', minHeight: '400px' }}>
                <SummaryChar loading={loading} data={summaryData} error={error} />
              </Box>

              {/* Tabbed Charts Section */}
              <Box
                sx={{
                  height: '450px',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: '350px',
                }}
              >
                <Tabs
                  value={activeTab}
                  onChange={handleTabChange}
                  sx={{
                    borderBottom: 1,
                    borderColor: 'divider',
                    '& .MuiTab-root': {
                      textTransform: 'none',
                      fontWeight: 'bold',
                      fontSize: '1rem',
                    },
                  }}
                >
                  <Tab label='Total Risk' />
                  <Tab label='Sector Composition' />
                </Tabs>

                {/* Tab Content */}
                <Box
                  sx={{
                    flex: 1,
                    p: 2,
                    backgroundColor: '#f9f9f9',
                  }}
                >
                  {activeTab === 0 && (
                    <RiskData data={riskData} loading={loading} error={error} />
                  )}
                  {activeTab === 1 && (
                    <SectorComp
                      data={sectorData}
                      loading={loading}
                      error={error}
                    />
                  )}
                </Box>
              </Box>
            </Box>

            {/* Right Column - Additional content (50% width) */}
            <Box
              sx={{
                width: '60%',
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
              }}
            >
              <Box
                sx={{
                  flex: 1,
                  backgroundColor: '#f9f9f9',
                  borderRadius: 1,
                  overflow: 'hidden',
                }}
              >
                <TrasReport
                  securities={securities}
                  loading={loading}
                  onSave={() => setSaveDialogOpen(true)}
                />
              </Box>
            </Box>
          </Box>
        </Box>

        <Dialog
          open={saveDialogOpen}
          onClose={handleCloseSaveDialog}
          PaperProps={{
            sx: {
              width: '100%',
              maxWidth: '600px',
              borderRadius: '8px',
            },
          }}
        >
          <DialogTitle sx={{ fontWeight: 'bold', fontSize: '1.5rem' }}>
            Save What-If Portfolio
          </DialogTitle>
          <DialogContent>
            <DialogContentText sx={{ mb: 2 }}>
              Enter a name to save the current What-If scenario as a new portfolio.
            </DialogContentText>
            <TextField
              autoFocus
              margin="dense"
              id="name"
              label="New Portfolio Name"
              type="text"
              fullWidth
              variant="outlined"
              value={newPortfolioName}
              onChange={(e) => {
                setNewPortfolioName(e.target.value)
                setIsTouched(true) // Mark as touched when user types
                if (validationError) {
                  setValidationError(null)
                }
              }}
              error={isTouched && !!validationError}
              helperText={isTouched && validationError}
            />
          </DialogContent>
          <DialogActions sx={{ p: '16px 24px' }}>
            <Button onClick={handleCloseSaveDialog} sx={{ mr: 1 }}>
              Cancel
            </Button>
            <Button
              onClick={handleSaveClick}
              disabled={!newPortfolioName}
              variant="contained"
            >
              Save
            </Button>
          </DialogActions>
        </Dialog>

        {notificationMessage && (
          <Box
            sx={{
              position: 'fixed',
              bottom: '20px',
              left: '50%',
              transform: 'translateX(-50%)',
              backgroundColor: '#57cd57ff',
              color: '#000000',
              padding: '12px 24px',
              borderRadius: '8px',
              zIndex: 1300,
              boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
              border: '1px solid #c8e6c9',
            }}
          >
            <Typography sx={{ fontWeight: 500 }}>{notificationMessage}</Typography>
          </Box>
        )}
      </>
    </StaticReportsLayout>
  )
}

export default WhatIfSummaryDashboard
